import React from 'react';
import { Card } from '@/components/UI';

type OverallStatus = 'ready' | 'not_ready' | 'needs_attention';

interface SystemChecksSummary {
    passed: number;
    failed: number;
    warning: number;
    notApplicable: number;
    overallStatus: OverallStatus;
}

interface StatusConfigItem {
    bg: string;
    border: string;
    text: string;
    label: string;
    icon: React.ReactNode;
}

interface Props {
    data: {
        summary: SystemChecksSummary;
    };
}

const getStatusConfig = (status: OverallStatus): StatusConfigItem => {
    switch (status) {
        case 'ready':
            return {
                bg: 'bg-green-900/30',
                border: 'border-green-500',
                text: 'text-green-400',
                label: 'Ready',
                icon: (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                    </svg>
                )
            };
        case 'not_ready':
            return {
                bg: 'bg-red-900/30',
                border: 'border-red-500',
                text: 'text-red-400',
                label: 'Not Ready',
                icon: (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                    </svg>
                )
            };
        case 'needs_attention':
            return {
                bg: 'bg-yellow-900/30',
                border: 'border-yellow-500',
                text: 'text-yellow-400',
                label: 'Needs Attention',
                icon: (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                        />
                    </svg>
                )
            };
    }
};

const ReadinessSummary: React.FC<Props> = ({ data }) => {
    const { summary } = data;

    const config = getStatusConfig(summary.overallStatus);
    const total = summary.passed + summary.failed + summary.warning;
    const percentage = total > 0 ? Math.round((summary.passed / total) * 100) : 0;

    return (
        <Card className={`${config.bg} border-l-4 ${config.border}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className={config.text}>{config.icon}</div>
                    <div>
                        <div className={`text-2xl font-bold ${config.text}`}>{config.label}</div>
                        <div className="text-zinc-400 text-sm">
                            {summary.passed} of {total} checks passed ({percentage}%)
                        </div>
                    </div>
                </div>
                <div className="flex gap-6">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-green-400">{summary.passed}</div>
                        <div className="text-xs text-zinc-500">Passed</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-yellow-400">{summary.warning}</div>
                        <div className="text-xs text-zinc-500">Warning</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-red-400">{summary.failed}</div>
                        <div className="text-xs text-zinc-500">Failed</div>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default ReadinessSummary;
